// @flow

import R from 'ramda';

import { ReduxAsyncAction } from '../support/utils';
import debugLib from 'debug';
import dash_api from '../support/dash_api';
import NotificationActions from './NotificationActions';
import { getPartnerName } from '../../main/screens/ScreenAdPartner/utils';

import type { AdPartner, AdPartnerBlueprint } from '../types/adPartnerTypes';

const debug = debugLib('AdPartnerActions');

const mergeEntityWithBlueprint = (
  blueprint: AdPartnerBlueprint,
  entityConfig: ?AdPartner
): AdPartner => {
  let mergedConfig;

  if (entityConfig) {
    mergedConfig = R.merge(blueprint, entityConfig);
  } else {
    /** Use blueprint only if entity's config doesn't exist
     *
     * ad_partner_blueprint entries have a different postback type than
     * ad_partner entries, so make sure to return the AdPartner type
     */
    mergedConfig = R.merge(blueprint, { postbacks: [] });
    mergedConfig.neverConfigured = true;
  }

  /**
   * Names of all events for all pre-defined postbacks are needed
   * because we use them to determine whether a URL is needed as input
   * while creating a postback.
   * If an event for which a postback is being created has a pre-defined
   * postback on the blueprint, then we wouldn't ask for a URL from the
   * user and use the pre-defined postback URL/template
   * If the event doesn't have a pre-defined postback on the blueprint, then
   * we try to fallback to custom_event on the blueprint. If custom_event
   * doesn't exist on the blueprint, then we need URL from the user
   */
  mergedConfig.eventsInBlueprintPostbacks = Object.keys(
    blueprint.postbacks || {}
  );

  return mergedConfig;
};

function mergeAppsWithBlueprints(
  blueprints: Array<AdPartnerBlueprint>,
  appConfigs: {
    [machine_name: string]: AdPartner
  }
): Array<AdPartner> {
  const mergedConfigs: Array<AdPartner> = blueprints.map(
    (blueprint: AdPartnerBlueprint): AdPartner => {
      // find appConfig based on machine_name
      const appConfig = appConfigs ? appConfigs[blueprint.machine_name] : null;
      return mergeEntityWithBlueprint(blueprint, appConfig);
    }
  );

  // There may be ad networks for which there are no blueprints
  const nonBlueprintedConfigs: Array<AdPartner> = Object.keys(
    appConfigs
  ).reduce((acc, machineName) => {
    if (
      !R.find(R.propEq('machine_name', machineName), blueprints) &&
      machineName.indexOf('a_custom_') !== -1 &&
      (appConfigs && appConfigs[machineName])
    ) {
      return [...acc, appConfigs[machineName]];
    }
    return acc;
  }, []);
  return mergedConfigs.concat(nonBlueprintedConfigs);
}

const GetAdPartners = new ReduxAsyncAction('AD_PARTNER_GET_PARTNER');
GetAdPartners.registerRequest(({ partner }) => {
  let appConfigs;

  return dash_api
    .get(`/v1/ad-networks/app`)
    .then(app_payload => {
      const { data } = app_payload;

      appConfigs = data;

      debug('App enabled partner list: ', appConfigs);
      return dash_api.get('/v1/ad-networks/global');
    })
    .then(global_payload => {
      const { data } = global_payload;
      const mergedConfigs = mergeAppsWithBlueprints(data, appConfigs);

      debug('Merged partners list: ', mergedConfigs);
      return {
        partners: mergedConfigs,
        selected: partner,
        hasPartnersLoaded: true
      };
    });
});

export const GET_PARTNERS = GetAdPartners.constants;
export const getAdPartners = GetAdPartners.request;

const GetAdPartnerForOrg = new ReduxAsyncAction(
  'AD_PARTNER_GET_PARTNER_FOR_ORG'
);
GetAdPartnerForOrg.registerRequest(machine_name => {
  let orgConfig;
  return dash_api
    .get(`/v1/ad-networks/org/${machine_name}`)
    .then(({ data }) => {
      orgConfig = data;
      return dash_api.get(`/v1/ad-networks/global-org/${machine_name}`);
    })
    .then(({ data }) => mergeEntityWithBlueprint(data, orgConfig));
});

export const GET_PARTNER_FOR_ORG = GetAdPartnerForOrg.constants;
export const getAdPartnerForOrg = GetAdPartnerForOrg.request;

/**
 * Fetch advertising_ontology
 * @return {Object} action
 */
const GetAdOntology = new ReduxAsyncAction('AD_PARTNER_GET_AD_ONTOLOGY');
GetAdOntology.registerRequest(() => {
  return dash_api.get(`/v1/ad-networks/ontology`).then(R.prop('data'));
});
export const GET_AD_ONTOLOGY = GetAdOntology.constants;
export const getAdOntology = GetAdOntology.request;

function trimPostbacks(partner) {
  const postbacks = partner.postbacks;

  const trimmedPostbacks = Array.isArray(postbacks)
    ? postbacks.map(p => {
        p.template = p.template && p.template.trim();
        return p;
      })
    : postbacks;

  return { ...partner, postbacks: trimmedPostbacks };
}

const SaveAdPartner = new ReduxAsyncAction('AD_PARTNER_SAVE_ENABLE');
SaveAdPartner.registerRequest(({ partner }) => {
  partner = trimPostbacks(partner);

  return dash_api.put(`/v1/ad-networks/app`, { partner }).then(payload => {
    const { data } = payload;
    data.machine_name = partner.machine_name;
    data.neverConfigured = false;

    partner.enabled &&
      NotificationActions.showNotification(
        `${getPartnerName(partner)} has been saved and enabled`,
        'success'
      );

    return data;
  });
});
export const SAVE_ENABLE = SaveAdPartner.constants;
export const saveAdPartner = SaveAdPartner.request;

const DeleteInternalPartner = new ReduxAsyncAction(
  'AD_PARTNER_DELETE_INTERNAL'
);
DeleteInternalPartner.registerRequest(({ partner }) => {
  const params = partner ? { params: { app_id: partner.app_id } } : {};
  return dash_api
    .delete(`/v1/ad-networks/app/${partner.machine_name}`, params)
    .then(res => {
      return dash_api.get('/v1/ad-networks/app').then(enabledPartners => {
        return dash_api
          .get(`/v1/ad-networks/global/${partner.machine_name}`)
          .then(partnerBlueprint => {
            return {
              partnerBlueprint: partnerBlueprint.data,
              enabledPartners: enabledPartners.data,
              partner
            };
          });
      });
    });
});
export const DELETE_INTERNAL_PARTNER = DeleteInternalPartner.constants;
export const deleteInternalPartner = DeleteInternalPartner.request;

const DisablePartner = new ReduxAsyncAction('AD_PARTNER_DISABLE');
DisablePartner.registerRequest(({ partner }) => {
  partner.enabled = false;

  return dash_api.put(`/v1/ad-networks/app`, { partner }).then(payload => {
    const { data } = payload;
    data.machine_name = partner.machine_name;

    NotificationActions.showNotification(
      `${getPartnerName(partner)} has been disabled`,
      'success'
    );

    return data;
  });
});
export const DISABLE = DisablePartner.constants;
export const disablePartner = DisablePartner.request;

const SavePostback = new ReduxAsyncAction('AD_PARTNER_SAVE_POSTBACK');
SavePostback.registerRequest(({ postback }) => {
  return dash_api
    .post(`/v1/ad-networks/postback`, {
      postback
    })
    .then(res => {
      return {
        partner: res.data,
        event: postback.event
      };
    });
});
export const SAVE_POSTBACK = SavePostback.constants;
export const savePostback = SavePostback.request;

const CreateInternalAdPartner = new ReduxAsyncAction(
  'AD_PARTNER_CREATE_INTERNAL'
);
CreateInternalAdPartner.registerRequest(({ internalPartner }) => {
  return dash_api
    .post('/v1/ad-networks/internal-partner', { internalPartner })
    .then(res => {
      return {
        selectedPartner: res.data
      };
    });
});
export const CREATE_INTERNAL_AD_PARTNER = CreateInternalAdPartner.constants;
export const createInternalAdPartner = CreateInternalAdPartner.request;

const ResetSettings = new ReduxAsyncAction('AD_PARTNER_RESET_SETTINGS');
ResetSettings.registerRequest((machine_name, partner) => {
  const params = partner ? { params: { app_id: partner.app_id } } : {};
  return dash_api
    .delete(`/v1/ad-networks/app/${machine_name}`, params)
    .then(res => {
      return dash_api.get('/v1/ad-networks/app').then(enabledPartners => {
        return dash_api
          .get(`/v1/ad-networks/global/${machine_name}`)
          .then(partnerBlueprint => {
            return {
              partnerBlueprint: partnerBlueprint.data,
              enabledPartners: enabledPartners.data
            };
          });
      });
    });
});
export const RESET_SETTINGS = ResetSettings.constants;
export const resetSettings = ResetSettings.request;

export const SELECT = 'AD_PARTNER_SELECT';
export function selectPartner({ partner }: { partner: Object }) {
  debug(SELECT, partner);
  return {
    type: SELECT,
    data: partner
  };
}

export const UPDATE = 'AD_PARTNER_UPDATE';
export function updateConfig({ config }: { config: Object }) {
  debug(UPDATE, config);
  return {
    type: UPDATE,
    data: config
  };
}
